/* eslint-disable */
// Member Attendance card (iter-126j, slice 7 of EventDetail.jsx refactor).
// Renders the "Attendance" tab content: grouped roster with desktop +
// mobile layouts, status badges, admin status-select, responded date.
// iter-128b: horizontal section-chip strip at the top for quick jump-to
// scrolling on long rosters. Includes a "Not RSVP'd" chip that scrolls
// to the first pending/no-response player.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";


const NO_RESPONSE_KEY = "__no_response__";


export const MemberAttendanceCard = ({
  memberGrouped,
  attendance_stats,
  showAdminControls,
  handleAttendanceChange,
  getAttendanceStatusBadge,
  formatRespondedDate,
  // iter-127j: per-event section override controls
  sectionsById,
  handleMoveSection,
}) => {
  const scrollAreaRef = useRef(null);
  const sectionRefs = useRef({});
  const firstPendingRef = useRef(null);
  const chipRefs = useRef({});
  // iter-128b (post-testing): tracks the most-recent chip click so the
  // IntersectionObserver callback below can ignore transient "section X
  // scrolled past" pulses while a smooth-scroll animation is still in
  // flight. Without this suppression, clicking a chip whose section has
  // very few rows lets the *next* section's header enter the observer
  // band before the animation settles and steals the active highlight.
  const programmaticUntilRef = useRef(0);
  const [activeKey, setActiveKey] = useState(null);

  // Count first-pending target + total no-response count for the chip.
  const noResponseCount = useMemo(() => {
    let n = 0;
    for (const g of memberGrouped || []) {
      for (const r of g.rows || []) {
        const s = r.status;
        if (!s || s === "pending" || s === "no_response") n += 1;
      }
    }
    return n;
  }, [memberGrouped]);

  // Set default active chip to the first section on mount / when groups change.
  useEffect(() => {
    if (memberGrouped && memberGrouped.length > 0 && !activeKey) {
      setActiveKey(memberGrouped[0].section_id || memberGrouped[0].section_name);
    }
  }, [memberGrouped, activeKey]);

  // IntersectionObserver: highlight the chip for the section currently
  // visible at the top of the scroll area.
  useEffect(() => {
    const root =
      scrollAreaRef.current?.querySelector?.("[data-radix-scroll-area-viewport]") ||
      scrollAreaRef.current;
    if (!root) return;
    const observed = Object.entries(sectionRefs.current).filter(([, node]) => node);
    if (observed.length === 0) return;

    const observer = new IntersectionObserver(
      () => {
        // Ignore observer pulses while a click-driven smooth-scroll is
        // still in flight (see programmaticUntilRef).
        if (Date.now() < programmaticUntilRef.current) return;
        // Inspect the CURRENT geometry of every section header, not just
        // the entries whose intersection state just changed. This makes
        // the highlight deterministic on rapid scroll and picks the
        // section closest to (but not below) the top of the viewport.
        const rootRect = root.getBoundingClientRect();
        let best = null;
        let bestTop = Infinity;
        for (const [key, node] of Object.entries(sectionRefs.current)) {
          if (!node) continue;
          const r = node.getBoundingClientRect();
          // Section header is visible OR its top has scrolled just above
          // the viewport top — take the one closest to the top edge.
          const relTop = r.top - rootRect.top;
          if (relTop <= 8 && relTop > -r.height) {
            // Currently pinned at the top edge — this is the active one.
            if (Math.abs(relTop) < bestTop) {
              bestTop = Math.abs(relTop);
              best = key;
            }
          }
        }
        if (best) setActiveKey(best);
      },
      { root, rootMargin: "0px 0px -85% 0px", threshold: [0, 1] },
    );

    observed.forEach(([, node]) => observer.observe(node));
    return () => observer.disconnect();
  }, [memberGrouped]);

  // Auto-scroll the chip strip so the active chip stays visible.
  useEffect(() => {
    if (!activeKey) return;
    const chip = chipRefs.current[activeKey];
    if (chip && typeof chip.scrollIntoView === "function") {
      chip.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }
  }, [activeKey]);

  const scrollToKey = (key) => {
    const target =
      key === NO_RESPONSE_KEY ? firstPendingRef.current : sectionRefs.current[key];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    // Suppress observer-driven active-chip updates for ~700ms so the
    // clicked chip stays highlighted while smooth-scroll settles.
    programmaticUntilRef.current = Date.now() + 700;
    setActiveKey(key);
  };

  // First-pending tracker: we assign the ref to the first matching row
  // during render below.
  let firstPendingAssigned = false;

  return (
    <Card className="glass-card">
      <CardHeader className="border-b border-white/5 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg">Member Attendance</CardTitle>
          <div className="text-sm text-muted-foreground">
            {attendance_stats.attending} / {attendance_stats.total_members} confirmed
          </div>
        </div>
      </CardHeader>

      {/* iter-128b: horizontal section-chip strip. Sits above the scroll
          area so it stays visible while scrolling. */}
      {memberGrouped && memberGrouped.length > 1 && (
        <div
          className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar border-b border-white/5 px-3 py-2 bg-secondary/40"
          data-testid="section-chip-strip"
        >
          {memberGrouped.map((group) => {
            const key = group.section_id || group.section_name;
            const isActive = activeKey === key;
            const attending = group.rows.filter((r) => r.status === "attending").length;
            return (
              <button
                key={key}
                type="button"
                ref={(el) => { chipRefs.current[key] = el; }}
                onClick={() => scrollToKey(key)}
                className={[
                  "shrink-0 rounded-full px-3 py-1 text-[11px] uppercase tracking-wider font-semibold",
                  "border transition-colors whitespace-nowrap",
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-secondary/60 text-muted-foreground border-white/10 hover:text-foreground hover:border-white/20",
                ].join(" ")}
                data-testid={`section-chip-${key}`}
                aria-current={isActive ? "true" : undefined}
              >
                {group.section_name}
                <span className="ml-1.5 tabular-nums opacity-80">
                  {attending}/{group.rows.length}
                </span>
              </button>
            );
          })}
          {noResponseCount > 0 && (
            <button
              type="button"
              ref={(el) => { chipRefs.current[NO_RESPONSE_KEY] = el; }}
              onClick={() => scrollToKey(NO_RESPONSE_KEY)}
              className={[
                "shrink-0 rounded-full px-3 py-1 text-[11px] uppercase tracking-wider font-semibold",
                "border transition-colors whitespace-nowrap",
                activeKey === NO_RESPONSE_KEY
                  ? "bg-yellow-500/90 text-black border-yellow-500"
                  : "bg-yellow-500/10 text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/20",
              ].join(" ")}
              data-testid="section-chip-no-response"
            >
              Not RSVP&rsquo;d
              <span className="ml-1.5 tabular-nums opacity-90">{noResponseCount}</span>
            </button>
          )}
        </div>
      )}

      <CardContent className="p-0">
        <ScrollArea className="h-[500px]" ref={scrollAreaRef}>
          {/* Header row */}
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-2 border-b border-white/5 bg-secondary/20 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            <span>Player</span>
            <span className="w-24 text-right" data-testid="attendance-col-responded">Responded</span>
            <span className="w-24 text-right">Status</span>
            {showAdminControls && <span className="w-32 text-right">Update</span>}
          </div>
          {memberGrouped.map((group) => {
            const key = group.section_id || group.section_name;
            return (
            <div key={key} data-testid={`member-section-${key}`}>
              {/* iter-127k: section header — was `bg-primary/[0.06]`
                  which is only 6% opaque, so player names behind
                  bled through when the header stuck to the top of
                  the scroll area. Switched to a solid card-tinted
                  background + backdrop-blur so scrolled content
                  is completely obscured. */}
              <div
                ref={(el) => { sectionRefs.current[key] = el; }}
                data-section-key={key}
                className="px-3 py-1.5 bg-secondary/95 supports-[backdrop-filter]:bg-secondary/80 backdrop-blur-md border-b border-t border-primary/20 flex items-center justify-between sticky top-0 z-20"
              >
                <h4 className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                  {group.section_name}
                </h4>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {group.rows.filter(r => r.status === 'attending').length} / {group.rows.length}
                </span>
              </div>
              <div className="divide-y divide-white/5">
                {group.rows.map((player) => {
                  // iter-127j: the player's OTHER sections — i.e. all
                  // section_ids except the one currently rendering as
                  // their group. Admins/MDs see a "Move to section"
                  // dropdown when there's at least one alternative.
                  const altSections = (player.section_ids || [])
                    .filter((sid) => sid && sid !== group.section_id)
                    .map((sid) => ({ id: sid, name: sectionsById?.[sid]?.name || sid }))
                    .filter((s) => s.name);
                  // iter-128b: mark the FIRST row across all groups that
                  // has no valid RSVP so the "Not RSVP'd" chip has a
                  // scroll target.
                  const isPending =
                    !player.status || player.status === "pending" || player.status === "no_response";
                  const assignPendingRef =
                    isPending && !firstPendingAssigned ? (el) => {
                      firstPendingRef.current = el;
                    } : undefined;
                  if (isPending && !firstPendingAssigned) firstPendingAssigned = true;
                  return (
                  <div
                    key={player.player_id}
                    ref={assignPendingRef}
                    className="flex flex-col gap-2 sm:grid sm:grid-cols-[1fr_auto_auto_auto] sm:gap-3 sm:items-center px-3 py-2.5 hover:bg-secondary/20 transition-colors"
                    data-testid={`attendance-row-${player.player_id}`}
                  >
                    {/* Name + part name + (mobile-only) status badge */}
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-2 sm:block">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate leading-tight">{player.player_name}</p>
                          {player.part_name && (
                            <p className="text-[11px] text-muted-foreground truncate leading-tight">{player.part_name}</p>
                          )}
                        </div>
                        {/* Mobile-only inline status badge */}
                        <div className="sm:hidden shrink-0">
                          {getAttendanceStatusBadge(player.status)}
                        </div>
                      </div>
                      {player.notes && (
                        <p className="text-[11px] text-primary/80 italic mt-1 break-words" title={player.notes} data-testid={`player-note-${player.player_id}`}>
                          &ldquo;{player.notes}&rdquo;
                        </p>
                      )}
                      {/* Mobile-only responded line */}
                      <p className="sm:hidden text-[11px] text-muted-foreground mt-1 tabular-nums" data-testid={`attendance-responded-date-mobile-${player.player_id}`}>
                        Responded: {formatRespondedDate(player.updated_at) || "—"}
                      </p>
                      {/* iter-127j: Move-to-section dropdown (admins/MD only,
                          appears when player has more than one section). */}
                      {showAdminControls && altSections.length > 0 && handleMoveSection && (
                        <div className="mt-1.5">
                          <Select
                            value=""
                            onValueChange={(value) =>
                              handleMoveSection(player.player_id, value === "__reset" ? null : value)
                            }
                          >
                            <SelectTrigger
                              className="w-full sm:w-44 h-7 text-[11px] bg-secondary/30 border-white/10"
                              data-testid={`move-section-trigger-${player.player_id}`}
                            >
                              <SelectValue placeholder="Move to section…" />
                            </SelectTrigger>
                            <SelectContent>
                              {altSections.map((s) => (
                                <SelectItem
                                  key={s.id}
                                  value={s.id}
                                  data-testid={`move-section-option-${player.player_id}-${s.id}`}
                                >
                                  {s.name}
                                </SelectItem>
                              ))}
                              <SelectItem value="__reset" data-testid={`move-section-reset-${player.player_id}`}>
                                Reset to primary
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Desktop: responded date column */}
                    <div
                      className="hidden sm:block w-24 text-right text-[11px] text-muted-foreground tabular-nums"
                      data-testid={`attendance-responded-date-${player.player_id}`}
                      title={player.updated_at ? new Date(player.updated_at).toLocaleString() : "No response yet"}
                    >
                      {formatRespondedDate(player.updated_at)}
                    </div>

                    {/* Desktop: status badge column */}
                    <div className="hidden sm:flex w-24 justify-end">
                      {getAttendanceStatusBadge(player.status)}
                    </div>

                    {showAdminControls && (
                      <Select
                        value={player.status}
                        onValueChange={(value) => handleAttendanceChange(player.player_id, value)}
                      >
                        <SelectTrigger className="w-full sm:w-28 h-8 bg-secondary/50 text-xs" data-testid={`attendance-select-${player.player_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="attending">Attending</SelectItem>
                          <SelectItem value="not_attending">Not Attending</SelectItem>
                          <SelectItem value="maybe">Maybe</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );})}
              </div>
            </div>
          );})}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default MemberAttendanceCard;
